import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import QuotationServicePicker from '../pages/ProfilePages/components/QuotationServicePicker';
import ProjectServiceScope from './ProjectServiceScope';
import { formatAUD } from '../utils/formatCurrency';
import '../pages/ProfilePages/styles/QuotationForm.css';
import '../styles/CatalogServicePicks.css';

/** Services for a new project: + Add service (catalog → tier → task groups) and the picked cards. */
export default function CatalogServicePicks({ bookingId, picks, onChange, disabled = false }) {
  const [open, setOpen] = useState(false);

  const add = (pick) => {
    // One line per service: picking it again (e.g. another tier) replaces it.
    onChange([...(picks || []).filter((p) => p.catalogId !== pick.catalogId), pick]);
    setOpen(false);
  };

  const total = (picks || []).reduce((sum, p) => sum + (Number(p.unitPrice) || 0), 0);

  /** Operational-tasks edits for one picked service (stays on this project only). */
  const editScope = (catalogId, next) =>
    onChange(
      picks.map((p) => {
        if (p.catalogId !== catalogId) return p;
        const tasks = (next.tasks || []).map((t) => ({ ...t, groupName: t.groupName || 'Tasks' }));
        return {
          ...p,
          tasks,
          activeGroup: next.activeGroup,
          extraGroups: next.extraGroups,
          newGroupName: next.newGroupName,
          // Priced by task: the total follows the tasks that are kept.
          unitPrice: p.pricedByTasks
            ? tasks.reduce((sum, t) => sum + (Number(t.price) || 0), 0)
            : p.unitPrice,
        };
      }),
    );

  return (
    <div className="csp">
      <button
        type="button"
        className="csp-add"
        disabled={disabled}
        onClick={() => setOpen(true)}
      >
        + Add service
      </button>
      {/* Picks are only saved to the lead on create, so no client is needed to pick. */}
      {!(picks || []).length ? (
        <p className="csp-empty">No services yet. Pick one from the catalog.</p>
      ) : null}
      {(picks || []).map((pick) => {
        const groups = [
          ...new Set((pick.tasks || []).map((t) => t.groupName).filter(Boolean)),
        ];
        return (
          <div className="csp-item" key={pick.catalogId}>
          <div className="csp-card">
            <div>
              <strong>
                {pick.name}
                {pick.tierName ? <span> · {pick.tierName}</span> : null}
              </strong>
              <small>
                {(pick.tasks || []).length} tasks
                {groups.length ? ` · ${groups.join(', ')}` : ''}
                {pick.pricedByTasks ? ' · priced by task' : ''}
              </small>
            </div>
            <b>{formatAUD(pick.unitPrice || 0)}</b>
            <button
              type="button"
              disabled={disabled}
              onClick={() => onChange(picks.filter((p) => p.catalogId !== pick.catalogId))}
            >
              Remove
            </button>
          </div>
          <ProjectServiceScope
            scope={{
              tasks: pick.tasks || [],
              activeGroup: pick.activeGroup,
              extraGroups: pick.extraGroups,
              newGroupName: pick.newGroupName,
            }}
            onChange={(next) => editScope(pick.catalogId, next)}
          />
          </div>
        );
      })}
      {(picks || []).length ? (
        <p className="csp-total">
          {picks.length} service{picks.length > 1 ? 's' : ''} · Total {formatAUD(total)}
        </p>
      ) : null}
      {open
        ? createPortal(
            <QuotationServicePicker
              bookingId={bookingId}
              persist={false}
              zIndex={20010}
              onClose={() => setOpen(false)}
              onAdded={add}
            />,
            document.body,
          )
        : null}
    </div>
  );
}
